import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Breadcrumbs,
  Link,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useNotificationFeed } from '../../hooks/useNotificationFeed';
import RoleSwitcher from './RoleSwitcher';
import HeaderActions from './HeaderActions';
import { getAssistant } from '../../assistants/assistants';

// Map tool slugs to display names
const TOOL_NAMES = {
  'drift-accumulation': 'DREW',
};

// Breadcrumb labels for tool sub-pages (keys = path after /<assistant>/, one or two
// segments). Mirrors the sidebar item names so the top bar never shows a raw URL slug
// such as "Rollup", "Ia" or "Case-studies".
const SUBPAGE_LABELS = {
  surveys: 'D&A Perspectives Surveys',
  'case-studies': 'Industry Case Studies',
  reports: 'Reports',
  documents: 'Document Upload Portal',
  settings: 'Organization',
  actions: 'Actions',
  'role/itm': 'Individual Contributor Dashboard',
  'role/fls': 'FLS Dashboard',
  'role/dh': 'DH Dashboard',
  'role/executive': 'Executive Dashboard',
  'role/org-it': 'Org IT Portal',
  'rollup/trl-entry': 'OE Condition Entry Portal',
  'rollup/review-queue': 'L1/FLS Review Queue',
  'rollup/severity-calibration': 'L2/DH Approval Queue',
  'rollup/site': 'Site Dashboard',
  'rollup/regional': 'Regional Rollup',
  'rollup/enterprise': 'Enterprise Rollup',
  'ia/it-portal': 'IT Portal Inputs',
  'ia/trl-system': 'OE Condition Analytics',
  'ia/comparisons': 'Comparisons',
  'ia/health-messages': 'OE Health Alerts',
  'ia/reports-on-demand': 'Reports on Demand',
};

/**
 * Derive breadcrumb info from the current location pathname.
 * Returns null if not inside a tool route.
 */
function useToolBreadcrumb() {
  const { pathname } = useLocation();
  // P6-14: IA routes are /<assistantId>/... (DREW at /drew, HUGO at /hugo)
  const match = pathname.match(/^\/(drew|hugo)(?=\/|$)/);
  if (!match) return null;
  const slug = match[1];
  const assistant = getAssistant(slug);
  const toolName = assistant?.name || TOOL_NAMES[slug] || slug;
  const avatar = assistant ? assistant.avatarSmall || assistant.avatar : null;
  const rest = pathname.replace(`/${slug}`, '').replace(/^\//, '');
  let subPage = null;
  if (rest) {
    const segs = rest.split('/');
    subPage =
      SUBPAGE_LABELS[segs.slice(0, 2).join('/')] ||
      SUBPAGE_LABELS[segs[0]] ||
      segs[0].charAt(0).toUpperCase() + segs[0].slice(1);
  }
  return { toolName, toolPath: `/${slug}`, subPage, avatar };
}

/**
 * Get a human-readable relative time string
 */
function getRelativeTime(date) {
  if (!date) return '';
  const now = new Date();
  const diff = now - date;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * Get icon for notification type
 */
function getNotificationIcon(type) {
  switch (type) {
    case 'threshold_exceeded':
      return <WarningAmberIcon color="warning" fontSize="small" />;
    case 'survey_created':
    case 'survey_published':
      return <AssignmentIcon color="primary" fontSize="small" />;
    case 'success':
      return <CheckCircleOutlineIcon color="success" fontSize="small" />;
    default:
      return <InfoOutlinedIcon color="info" fontSize="small" />;
  }
}

function TopBar({ onMenuClick, drawerWidth, isEntry = false }) {
  const { themeMode, toggleTheme } = useThemeMode();
  const muiTheme = useTheme();
  // Below `md` (tablet portrait / phone) the bar keeps wordmark · DREW badge · Beta menu · bell ·
  // avatar; settings, theme and View-as live in the avatar menu (UI review 2026-09-21 §3).
  const small = useMediaQuery(muiTheme.breakpoints.down('md'));
  // Below `xl` (1536) the three labelled pills only fit if View-as is icon-only and the display
  // name is hidden — a 1440 px laptop with the sidebar open has ~1150 px for the bar.
  const tight = useMediaQuery(muiTheme.breakpoints.down('xl'));
  const hideSubPage = tight;
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotificationFeed();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const toolBreadcrumb = useToolBreadcrumb();

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNotifOpen = (event) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login', { replace: true });
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read (pass the full object — shared org notifications
    // track read-state locally, personal ones on the doc itself)
    if (!notification.read) {
      await markAsRead(notification);
    }
    // Navigate to linked content if available
    if (notification.link) {
      navigate(notification.link);
      handleNotifClose();
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const userInitials = user?.displayName
    ? user.displayName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)
    : (user?.email?.charAt(0) || '?').toUpperCase();
  const notifOpen = Boolean(notifAnchorEl);

  // Show at most 20 recent notifications in the dropdown
  const recentNotifications = notifications.slice(0, 20);

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        ml: `${drawerWidth}px`,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: 'width 0.3s, margin-left 0.3s',
      }}
    >
      <Toolbar sx={{ gap: { xs: 0.25, sm: 0.5 }, px: { xs: 1, sm: 2 } }}>
        {/* The entry page has no sidebar (P6-01), so no hamburger there — a dead control on the
            first screen was the first thing the UI review flagged. */}
        {!isEntry && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: { xs: 0.5, sm: 1.5 } }}
            aria-label="Toggle sidebar"
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* REPSit wordmark */}
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
          onClick={() => navigate('/')}
          title="REPSit.ai home"
        >
          REPSit
        </Typography>
        {/* Always-on beta marker beside the wordmark; the ribbon itself is dismissible (⚑ D-UI-02). */}
        <Chip
          label="BETA"
          size="small"
          sx={{ ml: 1, height: 18, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 1, bgcolor: 'rgba(255,255,255,0.22)', color: 'inherit', display: { xs: 'none', sm: 'inline-flex' } }}
        />

        {/* Current tool name breadcrumb — with the IA's avatar as the DREW badge (P6-05, C0826:
            "the graphic in the corner" tells users they are in DREW without reading) */}
        {toolBreadcrumb && (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'inherit', opacity: 0.7 }} />}
            sx={{
              ml: { xs: 1, sm: 2 },
              color: 'inherit',
              minWidth: 0,
              overflow: 'hidden',
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& .MuiBreadcrumbs-li': { color: 'inherit', minWidth: 0 },
            }}
            aria-label="Tool breadcrumb"
          >
            <Link
              component="button"
              underline="hover"
              color="inherit"
              sx={{ opacity: 0.95, fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={() => navigate(toolBreadcrumb.toolPath)}
              aria-label={`${toolBreadcrumb.toolName} home`}
            >
              {toolBreadcrumb.avatar && (
                <Box
                  component="img"
                  src={toolBreadcrumb.avatar}
                  alt=""
                  className="ia-badge"
                  sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'common.white', boxShadow: 1 }}
                />
              )}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {toolBreadcrumb.toolName}
              </Box>
            </Link>
            {toolBreadcrumb.subPage && !hideSubPage && (
              <Typography color="inherit" noWrap sx={{ fontSize: '0.95rem', maxWidth: 260 }}>
                {toolBreadcrumb.subPage}
              </Typography>
            )}
          </Breadcrumbs>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Getting Started · Help & Docs · Tell the Build Team — in the page header on every page (Tim FAQ, D03 §A.1; P6-01/P6-10) */}
        <HeaderActions />

        {/* View-as stays a labelled control on desktop pages; on the entry page and on small
            screens it is reachable from the avatar menu (one first screen, few controls). */}
        {!small && !isEntry && <RoleSwitcher compact={tight} />}

        {/* Settings gear (Tim, C0814: gear → Settings — Your Settings / Organization / DREW).
            Shown on the entry page too (Tim 21 Oct: no visible way from the first screen to
            Settings → Organization → Import; the User Guide A.3 promises the gear on every page). */}
        {!small && (
          <Tooltip title="Settings">
            <IconButton color="inherit" onClick={() => navigate('/settings')} aria-label="Settings" data-tour="header-settings">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}

        {!small && !isEntry && (
          <Tooltip title={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton color="inherit" onClick={toggleTheme} aria-label="Toggle theme">
              {themeMode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Notifications">
          <IconButton
            color="inherit"
            aria-label="Notifications"
            onClick={handleNotifOpen}
            data-tour="notifications-bell"
          >
            <Badge badgeContent={unreadCount} max={9} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Notification Dropdown */}
        <Popover
          open={notifOpen}
          anchorEl={notifAnchorEl}
          onClose={handleNotifClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                width: { xs: 'calc(100vw - 16px)', sm: 380 },
                maxHeight: 480,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllRead}
                sx={{ textTransform: 'none' }}
              >
                Mark all read
              </Button>
            )}
          </Box>
          <Divider />
          <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
            {recentNotifications.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  color: 'text.secondary',
                }}
              >
                <NotificationsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                <Typography variant="body2">No notifications yet</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {recentNotifications.map((notif, index) => (
                  <React.Fragment key={notif.id}>
                    <ListItem
                      onClick={() => handleNotificationClick(notif)}
                      sx={{
                        cursor: notif.link ? 'pointer' : 'default',
                        bgcolor: notif.read ? 'transparent' : 'action.hover',
                        '&:hover': {
                          bgcolor: 'action.selected',
                        },
                        py: 1.5,
                        px: 2,
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        {getNotificationIcon(notif.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: notif.read ? 400 : 600,
                                flex: 1,
                              }}
                            >
                              {notif.message}
                            </Typography>
                            {!notif.read && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {getRelativeTime(notif.createdAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < recentNotifications.length - 1 && (
                      <Divider component="li" variant="inset" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Popover>

        <Tooltip title="Account">
          <Box
            onClick={handleMenuOpen}
            sx={{
              ml: { xs: 0, sm: 1 },
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              borderRadius: 2,
              px: { xs: 0.5, sm: 1 },
              py: 0.5,
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
            aria-label="User menu"
          >
            <Avatar src={user?.photoURL || undefined} sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.875rem' }}>
              {userInitials}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                ml: 1,
                color: 'inherit',
                fontWeight: 500,
                maxWidth: 120,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: { xs: 'none', xl: 'block' },
              }}
            >
              {displayName}
            </Typography>
          </Box>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { minWidth: 180, mt: 0.5 },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || ''}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); toggleTheme(); }}>
            <ListItemIcon>{themeMode === 'light' ? <Brightness4Icon fontSize="small" /> : <Brightness7Icon fontSize="small" />}</ListItemIcon>
            {themeMode === 'light' ? 'Dark mode' : 'Light mode'}
          </MenuItem>
          {(small || isEntry) && (
            <Box sx={{ px: 1 }}>
              <RoleSwitcher asMenuItem onNavigate={handleMenuClose} />
            </Box>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
